import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orderApi } from '../api/order-api';
import type { ChangeOrderStatusRequest, OrderListQuery } from './types';

export const orderKeys = {
  all: ['orders'] as const,
  list: (q: OrderListQuery) => [...orderKeys.all, 'list', q] as const,
  detail: (id: string) => [...orderKeys.all, 'detail', id] as const,
};

export function useOrdersQuery(query: OrderListQuery) {
  return useQuery({
    queryKey: orderKeys.list(query),
    queryFn: () => orderApi.list(query),
    staleTime: 30_000,
  });
}

export function useOrderQuery(id: string | undefined) {
  return useQuery({
    queryKey: orderKeys.detail(id ?? ''),
    queryFn: () => orderApi.get(id!),
    enabled: Boolean(id),
  });
}

export function useChangeOrderStatusMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ChangeOrderStatusRequest) => orderApi.changeStatus(id, body),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(id), data);
      void qc.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

export function useAttachOrderDocumentMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) => orderApi.attachDocument(id, mediaId),
    onSuccess: (data) => qc.setQueryData(orderKeys.detail(id), data),
  });
}

export function useDetachOrderDocumentMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) => orderApi.detachDocument(id, mediaId),
    onSuccess: (data) => qc.setQueryData(orderKeys.detail(id), data),
  });
}
